import React from 'react'
import clsx from 'clsx'
import DOMPurify from 'dompurify'
import Editor from '../../organisms/LexicalEditor'
import { TextareaAutosize } from '@mui/material'
import { Clear } from '@mui/icons-material'
import { useAppSelector } from '@SonarCX/hooks/redux'
import { useInView } from 'react-hook-inview'

import Folder2Icon from '../../icons/Folder2Icon'
import Tag2Icon from '../../icons/Tag2Icon'

import TagInputV2 from '../TagInputv2'
import MediaAvatar from '../../atoms/MediaAvatar'
import FolderSelectMenu from '../FolderSelectMenu'

import { getMedia } from '@SonarCX/lib/api'
import { formatDateRelative, getURLFromBlob } from '@SonarCX/utilities'
import { selectArticles } from '@SonarCX/slices/article'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import useStyles from './styles'

const DEFAULT_ARTICLE = {
  archived_at: null,
  body: null,
  deleted_at: null,
  id: null,
  items: [],
  media: [],
  media_ids: [],
  parent: null,
  subtitle: null,
  tags: [],
  title: null,
  total_items: 0,
  type: 'article'
}

const ArticleContent = ({ editingValues, onEdit }) => {
  const [toolbarInViewRef, toolbarInView] = useInView()

  const {
    selected,
    articles,
    editing: articleEditing,
    creating
  } = useAppSelector(selectArticles)
  const { allUsers, loadUser } = useUserAPI()
  const { currentUser } = useAgent()

  const article = React.useMemo(
    () => (creating ? DEFAULT_ARTICLE : articles[selected]),
    [articles, selected, creating]
  )

  const [creator, setCreator] = React.useState(null)
  const [lastUpdater, setLastUpdater] = React.useState(null)

  const sanitized = React.useMemo(
    () =>
      DOMPurify.sanitize(creating ? `<p></p>` : article.body, {
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling']
      }),
    [article.body, creating]
  )

  const [imageBlobs, setImageBlobs] = React.useState([])
  const [loadedMedia, setLoadedMedia] = React.useState(false)

  // Update position of toolbar on render
  if (articleEditing && !toolbarInView) {
    const element = document.querySelector('.toolbar')
    if (element) {
      element.setAttribute('style', `z-index: 100;position: fixed;top:80px;`)
    }
  } else if (articleEditing && toolbarInView) {
    const element = document.querySelector('.toolbar')
    if (element) {
      element.setAttribute('style', '')
    }
  }

  // Fixes images
  const body = React.useMemo(() => {
    const el = document.createElement('html')
    el.innerHTML = sanitized

    // Add correct image source
    const imgs = el.getElementsByTagName('img')
    for (let i = 0; i < imgs.length; i++) {
      const img = imgs[i]
      img.src = imageBlobs[i] ?? ''
    }

    return el.innerHTML
  }, [sanitized, imageBlobs])

  const editing = articleEditing || creating
  const articleTags = editing ? editingValues.tags : article.tags

  const classes = useStyles()

  // Trigger reload after ids are changed
  React.useEffect(() => {
    setLoadedMedia(false)
  }, [article.media_ids])

  // Gets all media
  React.useEffect(() => {
    if (loadedMedia) {
      return
    }

    const newMedia = {}

    Promise.all(
      // API has a bug that makes it so the ids in media_ids and media don't match
      article.media_ids.map(async (id, i) => {
        const fullMedia = await getMedia(id)

        const mediaObj = article.media[i]
        const blob = new Blob([fullMedia], { type: mediaObj.mime })
        const url = await getURLFromBlob(blob)

        newMedia[id] = url
      })
    ).then(() => {
      const newUrls = []

      article.media_ids.forEach(id => {
        newUrls.push(newMedia[id])
      })

      setImageBlobs(newUrls)
      setLoadedMedia(true)
    })
  }, [loadedMedia, article.media, article.media_ids])

  // Loads article creator if needed
  React.useEffect(() => {
    const id = creating ? currentUser()?.id : article.created_by
    const user = allUsers[id]

    if (!user) {
      loadUser(id)
    } else {
      setCreator(user)
    }
  }, [article.created_by, allUsers, loadUser, creating, currentUser])

  // Loads last updater if needed
  React.useEffect(() => {
    const id = creating ? currentUser()?.id : article.last_updated_by
    const user = allUsers[id]

    if (!user) {
      loadUser(id)
    } else {
      setLastUpdater(user)
    }
  }, [article.last_updated_by, allUsers, loadUser, creating, currentUser])

  const removeTag = React.useCallback(
    (tag, i) => {
      const editedTags = articleTags.filter((t, index) => index !== i)
      onEdit('tags', editedTags)
    },
    [articleTags, onEdit]
  )

  const addTag = React.useCallback(
    tag => {
      const editedTags = [...articleTags, tag]
      onEdit('tags', editedTags)
    },
    [articleTags, onEdit]
  )

  return (
    <div className={classes.root}>
      {/* {!editing && (
        <div className={classes.overview}>
          <div className={classes.overviewTitle}>Overview</div>

          <div className={classes.overviewStat}>
            <div>0</div>

            <div>Views</div>
          </div>

          <div className={classes.overviewStat}>
            <div>0</div>

            <div>Thumbs Up</div>
          </div>

          <div className={classes.overviewStat}>
            <div>0</div>

            <div>Thumbs Down</div>
          </div>
        </div>
      )} */}

      <div className={classes.editorInfo}>
        <MediaAvatar
          user={creating || article.created_by ? creator : null}
          className={classes.avatar}
        />

        <div className={classes.info}>
          <div className={classes.infoLine}>
            Written by{' '}
            <span>
              {creating || article.created_by
                ? creator?.name ?? 'Loading...'
                : 'No one'}
            </span>
          </div>

          <div className={classes.infoLine}>
            Last updated{' '}
            {creating
              ? 'just now'
              : article.updated_at
              ? formatDateRelative(article.updated_at, true)
              : 'just now '}{' '}
            by{' '}
            {creating || article.last_updated_by ? (
              <span>{lastUpdater?.name ?? 'Loading...'}</span>
            ) : (
              <span>No one</span>
            )}
          </div>
        </div>
      </div>

      <div className={classes.metaSection}>
        <div className={classes.metaTitleBlock}>
          <Folder2Icon className={clsx(classes.icon, classes.folderIcon)} />

          <div className={classes.metaTitle}>Folder</div>
        </div>

        {editing ? (
          <FolderSelectMenu
            value={editingValues.parent_id}
            onSelect={id => onEdit('parent_id', id)}
          />
        ) : (
          <div className={clsx(classes.box, classes.defaultCursor)}>
            {article.parent?.title ?? 'Unassigned'}
          </div>
        )}
      </div>

      <div className={classes.metaSection}>
        <div className={classes.metaTitleBlock}>
          <Tag2Icon className={clsx(classes.icon, classes.tagIcon)} />

          <div className={classes.metaTitle}>Tags</div>
        </div>

        <div className={classes.tagList}>
          {editing && (
            <TagInputV2 placeholder='' handleAddTag={addTag} width={200} />
          )}

          {articleTags.length > 0
            ? articleTags.map((tag, i) => (
                <div
                  key={`${tag}-${i}`}
                  className={clsx(classes.box, classes.defaultCursor)}
                >
                  <Tag2Icon
                    fill='#8F8F8F'
                    className={clsx(classes.icon, classes.tagIcon)}
                  />

                  {tag}
                  {editing && (
                    <Clear
                      onClick={() => {
                        removeTag(tag, i)
                      }}
                      fill='#8F8F8F'
                      className={clsx(classes.removeTagIcon, classes.muiIcon)}
                    />
                  )}
                </div>
              ))
            : !editing && (
                <div className={clsx(classes.box, classes.defaultCursor)}>
                  No tags
                </div>
              )}
        </div>
      </div>
      {editing ? (
        <>
          <div className={classes.inputBlock}>
            <input
              name='article-title'
              value={editingValues.title}
              className={classes.documentTitle}
              onChange={e => {
                onEdit('title', e.target.value)
              }}
              placeholder='Article title'
              maxLength={150}
              autoFocus
            />

            <label
              htmlFor='article-title'
              className={classes.documentTitleWordCounter}
            >
              ({150 - (editingValues.title?.length ?? 0)})
            </label>
          </div>

          <div className={clsx(classes.inputBlock, classes.inputBlockBorder)}>
            <div className={classes.subTitleHolder}>
              <TextareaAutosize
                name='article-subtitle'
                value={editingValues.subtitle}
                className={classes.documentSubTitle}
                onChange={e => {
                  onEdit('subtitle', e.target.value)
                }}
                placeholder='Short description'
                maxLength={300}
                maxRows={3}
              />
            </div>

            <label
              htmlFor='article-subtitle'
              className={classes.documentSubTitleWordCounter}
            >
              ({300 - (editingValues.subtitle?.length ?? 0)})
            </label>
          </div>
        </>
      ) : (
        <>
          <div className={classes.title}>{article.title}</div>

          <div className={classes.subtitle}>{article.subtitle}</div>
        </>
      )}

      {editing ? (
        <>
          <div ref={toolbarInViewRef} />

          <Editor
            autoFocus
            enableDrag
            value={body}
            onChange={value => onEdit('body', value)}
          />
        </>
      ) : (
        <div
          dangerouslySetInnerHTML={{ __html: body }}
          className={classes.content}
        />
      )}
    </div>
  )
}

export default ArticleContent
